import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { FaPencilAlt } from 'react-icons/fa';

const interests = [
  'Restaurants',
  'Shopping',
  'Entertainment',
  'Outdoor Activities',
  'Arts & Culture',
  'Nightlife',
  'Sports',
  'Fitness',
  'Education',
  'Health & Wellness'
];

export default function CompleteProfile() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, loading: profileLoading, updateProfile } = useProfile();
  
  const [zipCode, setZipCode] = useState('');
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isZipEditable, setIsZipEditable] = useState(false);

  // Load existing profile data when available
  useEffect(() => {
    if (profile) {
      if (profile.zip_code) {
        setZipCode(profile.zip_code);
      }
      if (profile.interests) {
        setSelectedInterests(profile.interests);
      }
    }
  }, [profile]);

  const handleInterestToggle = (interest: string) => {
    setSelectedInterests(prev =>
      prev.includes(interest)
        ? prev.filter(i => i !== interest)
        : [...prev, interest]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsLoading(true);
    try {
      await updateProfile({
        zip_code: zipCode,
        interests: selectedInterests
      });
      navigate('/listings');
    } catch (error: any) {
      // Error is handled in updateProfile
      console.error('Error in handleSubmit:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/listings');
  };

  const toggleZipEditable = () => {
    setIsZipEditable(!isZipEditable);
  };

  // Determine if the zip code should be disabled (read-only)
  const isZipDisabled: boolean = Boolean(profile?.zip_code) && !isZipEditable;

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-100 dark:from-amber-900 dark:to-orange-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-8">
            {profile && profile.zip_code ? 'Update Your Profile' : 'Complete Your Profile'}
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Home Zip Code
              </label>
              <div className="mt-1 relative flex items-center">
                <input
                  type="text"
                  id="zipCode"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  disabled={isZipDisabled}
                  pattern="[0-9]{5}"
                  placeholder="Enter your zip code"
                  className={`block w-full rounded-md shadow-sm focus:border-orange-500 focus:ring-orange-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                    isZipDisabled
                      ? 'border-gray-300 bg-gray-50 dark:bg-gray-800 cursor-not-allowed' 
                      : 'border-gray-300'
                  }`}
                />
                {profile?.zip_code && (
                  <button
                    type="button"
                    onClick={toggleZipEditable}
                    className="absolute right-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  >
                    <FaPencilAlt className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                What are you interested in?
              </label>
              <div className="grid grid-cols-2 gap-3">
                {interests.map((interest) => (
                  <button
                    key={interest}
                    type="button"
                    onClick={() => handleInterestToggle(interest)}
                    className={`p-3 rounded-md text-sm font-medium ${
                      selectedInterests.includes(interest)
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    {interest}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col space-y-3">
              <button
                type="submit"
                disabled={isLoading || profileLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : 'Save Profile'}
              </button>
              
              <button
                type="button"
                onClick={handleSkip}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                Skip for Now
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
} 